import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemButton,
  IconButton,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
  SvgIconProps,
} from '@mui/material';
import {
  Note as NoteIcon,
  Checklist as ListIcon,
  Description as DocumentIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Label as TagIcon,
  Badge as IdLegalIcon,
  LocalHospital as MedicalIcon,
  School as SchoolIcon,
  AccountBalance as FinancialIcon,
  HealthAndSafety as InsuranceIcon,
  Folder as OtherIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard, SearchInput } from '../../components/common';
import { NoteFormDialog, ListFormDialog } from '../../components/dialogs';
import { DocumentUploadDialog } from '../../components/dialogs/DocumentUploadDialog';
import { useFamilyData } from '../../hooks';
import { deleteFamilyData } from '../../services/dataApi';
import { getMediaFilesByFileIdDownloadUrl } from '../../services/api/familypal';
import { colors } from '../../theme';

interface DocumentData {
  name: string;
  description?: string;
  category: 'id_legal' | 'medical' | 'school' | 'financial' | 'insurance' | 'other';
  tags?: string[];
  file_id?: string;
}

const CATEGORY_CONFIG: Record<
  DocumentData['category'],
  { label: string; icon: React.ComponentType<SvgIconProps>; color: string }
> = {
  id_legal: { label: 'ID & Legal', icon: IdLegalIcon, color: '#5D9CEC' },
  medical: { label: 'Medical', icon: MedicalIcon, color: '#DC5D72' },
  school: { label: 'School', icon: SchoolIcon, color: '#EDA745' },
  financial: { label: 'Financial', icon: FinancialIcon, color: '#A0BC60' },
  insurance: { label: 'Insurance', icon: InsuranceIcon, color: '#AC8EB8' },
  other: { label: 'Other', icon: OtherIcon, color: '#9CA3AF' },
};

const CATEGORY_ORDER: DocumentData['category'][] = [
  'id_legal', 'medical', 'school', 'financial', 'insurance', 'other',
];

const OrganizationPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');

  // Note dialog states
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<any>(null);

  // List dialog states
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<any>(null);

  // Document states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<DocumentData['category'] | 'all'>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { items: notes, loading: notesLoading, insert: insertNote, update: updateNote, remove: removeNote } = useFamilyData('note');
  const { items: lists, loading: listsLoading, insert: insertList, update: updateList, remove: removeList } = useFamilyData('list');
  const { items: docItems, loading: docsLoading, refetch: refetchDocs } = useFamilyData<DocumentData>('document');

  const loading = notesLoading || listsLoading || docsLoading;

  // --- Note handlers ---
  const handleOpenNewNote = () => { setSelectedNote(null); setNoteDialogOpen(true); };
  const handleOpenEditNote = (note: any) => { setSelectedNote(note); setNoteDialogOpen(true); };

  const handleSaveNote = async (data: { title: string; text: string }, familyIds: string[]) => {
    try {
      if (selectedNote?.id) {
        await updateNote(selectedNote.id, data);
        enqueueSnackbar('Note updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) { await insertNote(data, familyId); }
        enqueueSnackbar('Note created', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save note', { variant: 'error' });
      throw err;
    }
  };

  const handleDeleteNote = async () => {
    if (!selectedNote?.id) return;
    try {
      await removeNote(selectedNote.id);
      enqueueSnackbar('Note deleted', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete note', { variant: 'error' });
      throw err;
    }
  };

  // --- List handlers ---
  const handleOpenNewList = () => { setSelectedList(null); setListDialogOpen(true); };
  const handleOpenEditList = (list: any) => { setSelectedList(list); setListDialogOpen(true); };

  const handleSaveList = async (data: { title: string; items: any[] }, familyIds: string[]) => {
    try {
      if (selectedList?.id) {
        await updateList(selectedList.id, data);
        enqueueSnackbar('List updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) { await insertList(data, familyId); }
        enqueueSnackbar('List created', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save list', { variant: 'error' });
      throw err;
    }
  };

  const handleDeleteList = async () => {
    if (!selectedList?.id) return;
    try {
      await removeList(selectedList.id);
      enqueueSnackbar('List deleted', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete list', { variant: 'error' });
      throw err;
    }
  };

  // --- Document handlers ---
  const documents = useMemo(() => {
    return docItems.map((item) => ({
      id: item.id,
      dataId: item.data?.id,
      data: item.data?.data,
      created_at: item.created_at,
      modified_at: item.modified_at,
    }));
  }, [docItems]);

  const filteredDocuments = useMemo(() => {
    let result = documents;
    if (selectedCategory !== 'all') {
      result = result.filter((doc) => doc.data?.category === selectedCategory);
    }
    if (search.trim() && tab === 2) {
      const lower = search.toLowerCase();
      result = result.filter(
        (doc) =>
          doc.data?.name?.toLowerCase().includes(lower) ||
          doc.data?.description?.toLowerCase().includes(lower) ||
          doc.data?.tags?.some((tag: string) => tag.toLowerCase().includes(lower))
      );
    }
    return result;
  }, [documents, selectedCategory, search, tab]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: documents.length };
    for (const cat of CATEGORY_ORDER) {
      counts[cat] = documents.filter((d) => d.data?.category === cat).length;
    }
    return counts;
  }, [documents]);

  const handleDownload = async (doc: (typeof documents)[0]) => {
    if (!doc.data?.file_id) {
      enqueueSnackbar('No file attached to this document', { variant: 'warning' });
      return;
    }
    setDownloadingId(doc.id);
    try {
      const response = await getMediaFilesByFileIdDownloadUrl({ path: { file_id: doc.data.file_id } } as any);
      const result = response.data as any;
      const url = result?.download_url || result?.downloadUrl || result?.url;
      if (!url) throw new Error('Failed to get download URL');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to get download URL', { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleEditDocument = (doc: (typeof documents)[0]) => {
    setSelectedDocument(docItems.find((i) => i.id === doc.id) || null);
    setUploadDialogOpen(true);
  };

  const handleDeleteDocument = async (doc: (typeof documents)[0]) => {
    if (!window.confirm(`Delete "${doc.data?.name}"? This cannot be undone.`)) return;
    try {
      await deleteFamilyData(doc.id);
      enqueueSnackbar('Document deleted', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to delete document', { variant: 'error' });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // --- Action button ---
  const handleAddAction = () => {
    if (tab === 0) handleOpenNewNote();
    else if (tab === 1) handleOpenNewList();
    else { setSelectedDocument(null); setUploadDialogOpen(true); }
  };

  const actionLabel = tab === 0 ? 'Add Note' : tab === 1 ? 'Add List' : 'Upload Document';

  if (loading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // --- Filtered notes/lists ---
  const filteredNotes = notes.filter((note) => {
    const data = (note.data?.data || {}) as any;
    return data.title?.toLowerCase().includes(search.toLowerCase()) ||
           data.text?.toLowerCase().includes(search.toLowerCase());
  });

  const filteredLists = lists.filter((list) => {
    const data = (list.data?.data || {}) as any;
    return data.title?.toLowerCase().includes(search.toLowerCase());
  });

  const totalIncompleteItems = lists.reduce((count, list) => {
    const data = (list.data?.data || {}) as any;
    const items = data.items || [];
    return count + items.filter((i: any) => !i.completed).length;
  }, 0);

  return (
    <Box>
      <PageHeader
        title="Notebook"
        subtitle="Notes, lists, and documents"
        actionLabel={actionLabel}
        actionIcon={AddIcon}
        onAction={handleAddAction}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataCard title="Notes" icon={NoteIcon} iconColor={colors.notes} value={notes.length} onClick={() => setTab(0)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataCard
            title="Lists"
            subtitle={totalIncompleteItems > 0 ? `${totalIncompleteItems} incomplete` : undefined}
            icon={ListIcon}
            iconColor="#4CAF50"
            value={lists.length}
            onClick={() => setTab(1)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DataCard title="Documents" icon={DocumentIcon} iconColor="#2563EB" value={documents.length} onClick={() => setTab(2)} />
        </Grid>

        <Grid size={12}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                  <Tab label={`Notes (${notes.length})`} />
                  <Tab label={`Lists (${lists.length})`} />
                  <Tab label={`Documents (${documents.length})`} />
                </Tabs>
              </Box>

              <Box sx={{ mb: 2 }}>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder={tab === 0 ? 'Search notes...' : tab === 1 ? 'Search lists...' : 'Search documents...'}
                />
              </Box>

              {/* Notes Tab */}
              {tab === 0 && (
                filteredNotes.length === 0 ? (
                  <EmptyState
                    icon={NoteIcon}
                    title={search ? 'No notes found' : 'No notes yet'}
                    description={search ? 'Try a different search' : 'Create notes to keep track of important information'}
                    actionLabel={search ? undefined : 'Add Note'}
                    onAction={search ? undefined : handleOpenNewNote}
                  />
                ) : (
                  <Grid container spacing={2}>
                    {filteredNotes.map((note) => {
                      const data = (note.data?.data || {}) as any;
                      return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={note.id}>
                          <Card
                            variant="outlined"
                            sx={{ height: '100%', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            onClick={() => handleOpenEditNote(note)}
                          >
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                {data.title || 'Untitled'}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
                              >
                                {data.text || 'No content'}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )
              )}

              {/* Lists Tab */}
              {tab === 1 && (
                filteredLists.length === 0 ? (
                  <EmptyState
                    icon={ListIcon}
                    title={search ? 'No lists found' : 'No lists yet'}
                    description={search ? 'Try a different search' : 'Create lists to track todo items'}
                    actionLabel={search ? undefined : 'Add List'}
                    onAction={search ? undefined : handleOpenNewList}
                  />
                ) : (
                  <List disablePadding>
                    {filteredLists.map((list) => {
                      const data = (list.data?.data || {}) as any;
                      const items = data.items || [];
                      const completedCount = items.filter((i: any) => i.completed).length;
                      return (
                        <ListItemButton
                          key={list.id}
                          onClick={() => handleOpenEditList(list)}
                          sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                          <ListItemText primary={data.title || 'Untitled List'} secondary={`${items.length} items`} />
                          <Chip
                            label={`${completedCount}/${items.length}`}
                            size="small"
                            color={completedCount === items.length && items.length > 0 ? 'success' : 'default'}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )
              )}

              {/* Documents Tab */}
              {tab === 2 && (
                <>
                  {/* Category sub-tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                      value={selectedCategory}
                      onChange={(_, val) => setSelectedCategory(val)}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab label={`All (${categoryCounts.all})`} value="all" />
                      {CATEGORY_ORDER.map((cat) => {
                        const config = CATEGORY_CONFIG[cat];
                        return (
                          <Tab
                            key={cat}
                            label={`${config.label} (${categoryCounts[cat] || 0})`}
                            value={cat}
                            disabled={categoryCounts[cat] === 0}
                          />
                        );
                      })}
                    </Tabs>
                  </Box>

                  {filteredDocuments.length === 0 ? (
                    <EmptyState
                      icon={DocumentIcon}
                      title={search || selectedCategory !== 'all' ? 'No documents found' : 'No documents yet'}
                      description={
                        !search && selectedCategory === 'all'
                          ? 'Upload important documents like IDs, medical records, and insurance cards'
                          : undefined
                      }
                      actionLabel={!search && selectedCategory === 'all' ? 'Upload Document' : undefined}
                      onAction={
                        !search && selectedCategory === 'all'
                          ? () => { setSelectedDocument(null); setUploadDialogOpen(true); }
                          : undefined
                      }
                    />
                  ) : (
                    <List disablePadding>
                      {filteredDocuments.map((doc, idx) => {
                        const data = doc.data;
                        if (!data) return null;
                        const catConfig = CATEGORY_CONFIG[data.category] || CATEGORY_CONFIG.other;
                        const Icon = catConfig.icon;
                        const isDownloading = downloadingId === doc.id;

                        return (
                          <ListItem key={doc.id} divider={idx < filteredDocuments.length - 1} sx={{ px: 0, py: 1.5 }}>
                            <ListItemIcon>
                              <Box
                                sx={{
                                  width: 40, height: 40, borderRadius: 2,
                                  bgcolor: catConfig.color + '20',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                <Icon sx={{ color: catConfig.color, fontSize: 20 }} />
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" fontWeight={600}>{data.name}</Typography>
                                  <Chip
                                    label={catConfig.label}
                                    size="small"
                                    sx={{ bgcolor: catConfig.color + '20', color: catConfig.color, fontWeight: 600, fontSize: '0.7rem' }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  {data.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }} noWrap>
                                      {data.description}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.disabled">{formatDate(doc.created_at)}</Typography>
                                    {data.tags?.map((tag: string) => (
                                      <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        icon={<TagIcon sx={{ fontSize: '12px !important' }} />}
                                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#EEF2FF', color: '#4F46E5', '& .MuiChip-icon': { color: '#4F46E5' } }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {data.file_id && (
                                  <Tooltip title="Download / View">
                                    <IconButton size="small" onClick={() => handleDownload(doc)} disabled={isDownloading}>
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleEditDocument(doc)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => handleDeleteDocument(doc)} sx={{ color: 'error.main' }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Note Form Dialog */}
      <NoteFormDialog
        open={noteDialogOpen}
        onClose={() => { setNoteDialogOpen(false); setSelectedNote(null); }}
        onSave={handleSaveNote}
        onDelete={selectedNote ? handleDeleteNote : undefined}
        note={selectedNote}
      />

      {/* List Form Dialog */}
      <ListFormDialog
        open={listDialogOpen}
        onClose={() => { setListDialogOpen(false); setSelectedList(null); }}
        onSave={handleSaveList}
        onDelete={selectedList ? handleDeleteList : undefined}
        list={selectedList}
      />

      {/* Document Upload / Edit Dialog */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onClose={() => { setUploadDialogOpen(false); setSelectedDocument(null); }}
        onSave={async () => {
          enqueueSnackbar(selectedDocument ? 'Document updated' : 'Document uploaded', { variant: 'success' });
          await refetchDocs();
        }}
        document={selectedDocument}
      />
    </Box>
  );
};

export default OrganizationPage;
